import {useThree} from "@react-three/fiber";
import React, {useEffect, useMemo, useState} from "react";
import * as THREE from "three";
import {RGBELoader} from "three/examples/jsm/loaders/RGBELoader.js";
import {store} from "../../../../Store/index.js";
import {observer_mgl_r3} from "../../../../Utils/ThreeJS/General.js";
import {BakedSun, GetKTX2Loader, LoadBakedSun, SetGlassEnvMap} from "./BakedLightmaps.js";

// Sky for the LHQ scene; mirrors the Blender world used for the lightmap bakes (see header of LHQ_lightmap_bake.py):
//   Environment Texture = HDRIs/<skyFile>, Mapping(POINT) Z-rotation = blenderSkyRotZ, BG_Camera strength = skyIntensity.
// Only the *visible* sky is reproduced here; the sky's lighting contribution is already in the baked lightmaps (BG_Lighting).
// Rotation mapping: Blender's equirect convention (z-up) coincides with three's (y-up, after the ZUpGroup's -90deg x-rotation),
// and three inverts scene.backgroundRotation before sampling, so a Blender Z-rotation of θ = a three.js Y-rotation of -θ.

/** Must match the Z-rotation (radians) of the Mapping node in the Blender world. */
export const blenderSkyRotZ = -1.5687717199325562; // turnberg_sky_5 (sun toward content +Y); was -2.2636921405792236 for kloppenheim_06

export const SkyBackground = observer_mgl_r3(()=>{
	const {skyFile} = store.main.tools.vr.scene_lhq;
	// ".ktx2" = upper-hemisphere dome (from Scripts/Blender/SkyPrep.py); else a regular equirect HDR
	if (skyFile.endsWith(".ktx2")) return <SkyDome key={skyFile}/>;
	return <SkyHDR/>;
});

const SkyHDR = observer_mgl_r3(()=>{
	const scene = useThree(s=>s.scene);
	const invalidate = useThree(s=>s.invalidate);
	const {skyFile, skyIntensity} = store.main.tools.vr.scene_lhq;
	useEffect(()=>{
		let disposed = false;
		let tex: THREE.DataTexture|n;
		new RGBELoader().loadAsync(skyFile).then(t=>{
			if (disposed) return void t.dispose();
			tex = t;
			t.mapping = THREE.EquirectangularReflectionMapping;
			scene.background = t;
			scene.backgroundIntensity = skyIntensity;
			scene.backgroundRotation.set(0, -blenderSkyRotZ, 0);
			SetGlassEnvMap(t, -blenderSkyRotZ); // sky reflections on baked glass
			invalidate();
		}, err=>console.warn("[SkyBackground] failed to load sky:", err));
		return ()=>{
			disposed = true;
			if (tex != null && scene.background == tex) scene.background = null;
			if (tex != null) SetGlassEnvMap(null, 0);
			tex?.dispose();
		};
	}, [scene, invalidate, skyFile, skyIntensity]);
	return null;
});

// dome mode: texture = upper hemisphere only (row 0 = zenith; sRGB UASTC of linear * encodeScale); below the horizon, the horizon row
// fades into a flat haze color (no ground visible from the tower). Rotated so the texture's sun (meta.sunU) faces the sun-lamp.

type SkyDomeMeta = {
	encodeScale: number, hazeColor: [number, number, number], hazeFadeDeg: number, reflectionFile?: string|n,
	/** Texture-u (0-1, left->right) of the sun; if set, the dome is rotated so the sun faces the sun-lamp's azimuth (else blenderSkyRotZ is used). */
	sunU?: number|n,
	/** Below-horizon strip (png, relative to the json; made by SkyPrep.py), and its row-count. Without it, the horizon row is just faded into hazeColor. */
	hazeStrip?: string|n,
	hazeRows?: number|n,
};

/** Y-rotation (three's convention: the value for `Euler(0, θ, 0)` applied to the view direction before the equirect lookup) that puts the
 * texture's sun (at u = sunU) in the azimuth of the sun-lamp (manifest sun, else content +Y). */
function SkyDomeRotY(meta: SkyDomeMeta, bakedSun: BakedSun|n) {
	if (meta.sunU == null) return blenderSkyRotZ;
	// toward-sun direction, in content (z-up) space; the lamp's dir is the direction the light travels, so negate it
	let [cx, cy] = bakedSun != null ? [-bakedSun.dir[0], -bakedSun.dir[1]] : [0, 1];
	if (Math.hypot(cx, cy) < 1e-4) [cx, cy] = [0, 1]; // sun straight overhead => no meaningful azimuth
	// ZUpGroup maps content (x, y, z) -> root (x, z, -y), and equirectUv's azimuth is atan2(root.z, root.x)
	const targetAz = Math.atan2(-cy, cx);
	const texSunAz = (meta.sunU - 0.5) * 2 * Math.PI; // azimuth equirectUv maps to u = sunU
	// Ry(θ) maps azimuth a -> a - θ, so we need targetAz - θ = texSunAz
	return targetAz - texSunAz;
}

const skyDomeVert = `
varying vec3 vDir;
void main() {
	vDir = position; // mesh has an identity transform (and is centered on the camera via the rotation-only view matrix below)
	gl_Position = projectionMatrix * mat4(mat3(viewMatrix)) * vec4(position, 1.0);
	gl_Position.z = gl_Position.w; // at the far plane (like three's background box)
}`;
const skyDomeFrag = `
#include <common>
uniform sampler2D skyTex;
uniform mat3 skyRot;
uniform float texScale; // skyIntensity / encodeScale
uniform vec3 hazeColor; // linear, already * skyIntensity
uniform float hazeFade; // radians below the horizon over which the horizon color fades into hazeColor
uniform sampler2D hazeTex; // below-horizon strip (v=0 at the horizon, v=1 at hazeFade below); blur widens continuously with depth
uniform float hazeRows;
uniform bool hasHazeTex;
varying vec3 vDir;
void main() {
	vec3 d = skyRot * normalize(vDir);
	float u = atan(d.z, d.x) * RECIPROCAL_PI2 + 0.5;
	float elev = asin(clamp(d.y, -1.0, 1.0));
	float v = 1.0 - max(elev, 0.0) / PI_HALF; // 0 = zenith, 1 = horizon

	// seam-safe u-derivatives (atan wraps at u=0/1, which would otherwise pick the smallest mip => a visible line)
	float u2 = fract(u + 0.5);
	float dux = dFdx(u), duy = dFdy(u), dux2 = dFdx(u2), duy2 = dFdy(u2);
	vec2 gx = vec2(abs(dux) < abs(dux2) ? dux : dux2, dFdx(v));
	vec2 gy = vec2(abs(duy) < abs(duy2) ? duy : duy2, dFdy(v));
	vec3 c;
	if (elev >= 0.0) {
		c = textureGrad(skyTex, vec2(u, v), gx, gy).rgb * texScale;
	} else {
		float t = clamp(-elev / hazeFade, 0.0, 1.0);
		vec3 horizon = textureGrad(skyTex, vec2(u, 1.0), gx, gy).rgb;
		vec3 below = horizon;
		if (hasHazeTex) {
			// row k = depth k/(rows-1); texel-centered so t=0/1 hit the first/last rows exactly (no mips => no atan-seam issue)
			vec3 strip = texture(hazeTex, vec2(u, (t * (hazeRows - 1.0) + 0.5) / hazeRows)).rgb;
			below = mix(horizon, strip, smoothstep(0.0, 0.08, t)); // hand off from the full-res horizon row, so the horizon line itself stays seamless
		}
		c = mix(below * texScale, hazeColor, smoothstep(0.0, 1.0, t));
	}

	gl_FragColor = vec4(c, 1.0);
	#include <tonemapping_fragment>
	#include <colorspace_fragment>
}`;

const SkyDome = observer_mgl_r3(()=>{
	const renderer = useThree(s=>s.gl);
	const invalidate = useThree(s=>s.invalidate);
	const {skyFile, skyIntensity} = store.main.tools.vr.scene_lhq;

	const geometry = useMemo(()=>new THREE.BoxGeometry(1, 1, 1), []);
	const material = useMemo(()=>new THREE.ShaderMaterial({
		vertexShader: skyDomeVert,
		fragmentShader: skyDomeFrag,
		uniforms: {
			skyTex: {value: null},
			skyRot: {value: new THREE.Matrix3()}, // set once the meta is loaded (see SkyDomeRotY)
			texScale: {value: 0},
			hazeColor: {value: new THREE.Color(0, 0, 0)},
			hazeTex: {value: null},
			hazeRows: {value: 1},
			hasHazeTex: {value: false},
			hazeFade: {value: 0.2},
		},
		side: THREE.BackSide,
		depthWrite: false,
	}), []);
	useEffect(()=>()=>{ geometry.dispose(); material.dispose(); }, [geometry, material]);

	const [meta, setMeta] = useState<SkyDomeMeta|n>(null);
	const [tex, setTex] = useState<THREE.Texture|n>(null);
	const [rotY, setRotY] = useState(0);
	useEffect(()=>{
		let disposed = false;
		let loadedTex: THREE.Texture|n;
		Promise.all([
			fetch(skyFile.replace(/\.ktx2$/, ".json")).then(a=>a.json()) as Promise<SkyDomeMeta>,
			GetKTX2Loader(renderer).loadAsync(skyFile),
			LoadBakedSun(), // waited for, so the dome doesn't visibly jump when the sun-direction arrives
		]).then(([m, t, sun])=>{
			if (disposed) return void t.dispose();
			loadedTex = t;
			t.wrapS = THREE.RepeatWrapping; // azimuth wraps
			t.wrapT = THREE.ClampToEdgeWrapping; // zenith/horizon don't
			t.anisotropy = renderer.capabilities.getMaxAnisotropy();
			t.needsUpdate = true;
			setRotY(SkyDomeRotY(m, sun));
			setMeta(m);
			setTex(t);
		}, err=>console.warn("[SkyBackground] failed to load sky dome:", err));
		return ()=>{
			disposed = true;
			loadedTex?.dispose();
		};
	}, [renderer, skyFile]);

	const [hazeTex, setHazeTex] = useState<THREE.Texture|n>(null);
	useEffect(()=>{
		if (meta?.hazeStrip == null) return;
		let disposed = false;
		let loaded: THREE.Texture|n;
		const url = new URL(meta.hazeStrip, new URL(skyFile, location.href)).href; // relative to the dome's json
		new THREE.TextureLoader().loadAsync(url).then(t=>{
			if (disposed) return void t.dispose();
			loaded = t;
			t.colorSpace = THREE.SRGBColorSpace;
			t.wrapS = THREE.RepeatWrapping;
			t.wrapT = THREE.ClampToEdgeWrapping;
			t.generateMipmaps = false; // blur is already baked in (and mips would pick the smallest level at the atan-seam)
			t.minFilter = THREE.LinearFilter;
			t.needsUpdate = true;
			setHazeTex(t);
		}, err=>console.warn("[SkyBackground] failed to load haze strip:", err));
		return ()=>{
			disposed = true;
			loaded?.dispose();
			setHazeTex(null);
		};
	}, [meta, skyFile]);

	// glass reflections: the dome texture is upper-half + LDR, so use the (small) HDR named in the meta instead
	useEffect(()=>{
		const reflectionFile = meta?.reflectionFile;
		if (reflectionFile == null) return;
		let disposed = false;
		let refl: THREE.DataTexture|n;
		new RGBELoader().loadAsync(reflectionFile).then(t=>{
			if (disposed) return void t.dispose();
			refl = t;
			t.mapping = THREE.EquirectangularReflectionMapping;
			// three negates material.envMapRotation before use, so -rotY yields the same Ry(rotY) as the dome (assumes the reflection HDR is the same image, ie. same sun-u)
			SetGlassEnvMap(t, -rotY);
			invalidate();
		}, err=>console.warn("[SkyBackground] failed to load reflection HDR:", err));
		return ()=>{
			disposed = true;
			if (refl != null) SetGlassEnvMap(null, 0);
			refl?.dispose();
		};
	}, [meta, rotY, invalidate]);

	useEffect(()=>{
		if (meta == null || tex == null) return;
		const u = material.uniforms;
		u.skyTex.value = tex;
		u.skyRot.value.setFromMatrix4(new THREE.Matrix4().makeRotationY(rotY));
		u.texScale.value = skyIntensity / meta.encodeScale;
		u.hazeColor.value.setRGB(...meta.hazeColor).multiplyScalar(skyIntensity);
		u.hazeTex.value = hazeTex;
		u.hazeRows.value = meta.hazeRows ?? 1;
		u.hasHazeTex.value = hazeTex != null;
		u.hazeFade.value = THREE.MathUtils.degToRad(meta.hazeFadeDeg);
		invalidate();
	}, [material, meta, tex, hazeTex, rotY, skyIntensity, invalidate]);

	if (tex == null) return null;
	return <mesh name="SkyDome" geometry={geometry} material={material} frustumCulled={false} renderOrder={-1000}/>;
});